import React, { useState, useRef, useEffect } from 'react';
import './VideoTest.css';

const VideoTest = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [hasVideo, setHasVideo] = useState(null); // null = серый (не проверено), true/false = результат
  const [hasAudio, setHasAudio] = useState(null);

  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const previewVideoRef = useRef(null);
  const playbackVideoRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoUrl]);

  // Проверка НЕ выполняется во время записи, только после просмотра

  const startRecording = async () => {
    try {
      setError(null);
      setVideoBlob(null);
      setVideoUrl(null);
      setRecordingTime(0);
      setHasVideo(null); // Сбрасываем проверки при новой записи
      setHasAudio(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true, 
        video: true 
      });
      
      streamRef.current = stream;
      
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        previewVideoRef.current.muted = true;
        previewVideoRef.current.play().catch(() => {});
      }
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      videoChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Сохраняем информацию о треках перед остановкой потока
        const videoTracks = streamRef.current ? streamRef.current.getVideoTracks() : [];
        const audioTracks = streamRef.current ? streamRef.current.getAudioTracks() : [];
        const hadVideo = videoTracks.length > 0 && videoTracks[0].enabled;
        const hadAudio = audioTracks.length > 0 && audioTracks[0].enabled;
        
        const videoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(videoBlob);
        setVideoBlob(videoBlob);
        setVideoUrl(url);
        
        // Останавливаем поток
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (previewVideoRef.current) {
          previewVideoRef.current.srcObject = null;
        }
        
        // Проверяем наличие видео и аудио в записи
        checkRecordingTracks(videoBlob, hadVideo, hadAudio);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Таймер
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError('Не удалось получить доступ к камере и микрофону. Проверьте разрешения.');
      console.error('Error accessing media devices:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const checkRecordingTracks = async (blob, hadVideo, hadAudio) => {
    try {
      const video = document.createElement('video');
      const url = URL.createObjectURL(blob);
      video.src = url;
      video.muted = true;
      
      let checkCompleted = false;
      
      const checkVideoContent = () => {
        // Если есть размеры видео, считаем что видео доступно
        // Проверка на черный экран может быть неточной из-за особенностей кодирования
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          return true;
        }
        return false;
      };
      
      const performCheck = () => {
        if (checkCompleted) return;
        checkCompleted = true;
        
        // Проверяем наличие видео по размерам и содержимому
        const hasVideoContent = checkVideoContent();
        setHasVideo(hasVideoContent && hadVideo);
        setHasAudio(hadAudio);
        
        // Освобождаем URL после проверки
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      };
      
      // Проверяем когда видео готово к воспроизведению
      video.oncanplay = () => {
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA или выше
          performCheck();
        }
      };
      
      // Также проверяем когда загружены метаданные
      video.onloadedmetadata = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          // Если видео уже готово, проверяем сразу
          if (video.readyState >= 2) {
            performCheck();
          }
        }
      };
      
      video.onerror = () => {
        URL.revokeObjectURL(url);
        setHasVideo(false);
        setHasAudio(false);
      };
      
      // Загружаем видео
      video.load();
      
      // Таймаут на случай, если события не сработают
      setTimeout(() => {
        if (!checkCompleted && video.readyState >= 1) {
          // Если есть размеры, считаем что видео есть
          const hasVideoContent = video.videoWidth > 0 && video.videoHeight > 0;
          setHasVideo(hasVideoContent && hadVideo);
          setHasAudio(hadAudio);
          checkCompleted = true;
          URL.revokeObjectURL(url);
        }
      }, 3000);
    } catch (err) {
      console.error('Error checking recording tracks:', err);
      setHasVideo(hadVideo);
      setHasAudio(hadAudio);
    }
  };

  const handleRerecord = () => {
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
    setVideoBlob(null);
    setVideoUrl(null);
    setRecordingTime(0);
    setHasVideo(null); // Сбрасываем проверки при перезаписи
    setHasAudio(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="video-test-container">
      <h2 className="video-test-title">Тест видеосвязи</h2>
      
      {error && <div className="video-test-error">{error}</div>}

      <div className="video-test-content">
        {/* Левая колонка: Запись + Кнопки */}
        <div className="video-test-column">
          {/* Окно с камерой и записью */}
          <div className="video-test-section">
            <h3 className="video-test-section-title">Запись</h3>
            <div className="video-preview-container">
              <video
                ref={previewVideoRef}
                className="video-preview"
                autoPlay
                muted
                playsInline
              />
              {!isRecording && !videoUrl && (
                <div className="video-placeholder">
                  Нажмите "Начать запись" для начала теста
                </div>
              )}
            </div>
          </div>

          {/* Кнопки управления записью */}
          <div className="video-controls">
            {!isRecording && !videoUrl ? (
              <button
                onClick={startRecording}
                className="btn-record-start"
              >
                <span className="record-icon">●</span>
                Начать запись
              </button>
            ) : isRecording ? (
              <button
                onClick={stopRecording}
                className="btn-record-stop"
              >
                <span className="stop-icon">■</span>
                Остановить запись
                <span className="recording-time">{formatTime(recordingTime)}</span>
              </button>
            ) : (
              <button
                onClick={handleRerecord}
                className="btn-record-rerecord"
              >
                Перезаписать
              </button>
            )}
          </div>
        </div>

        {/* Правая колонка: Просмотр + Проверки */}
        <div className="video-test-column">
          {/* Просмотр записи */}
          <div className="video-test-section">
            <h3 className="video-test-section-title">Просмотр записи</h3>
            <div className="video-playback-container">
              {videoUrl ? (
                <video
                  ref={playbackVideoRef}
                  src={videoUrl}
                  controls
                  className="video-playback"
                />
              ) : (
                <div className="video-placeholder">
                  Запись появится здесь после остановки
                </div>
              )}
            </div>
          </div>

          {/* Проверки */}
          <div className="video-checks">
            <div className="video-check-item">
              <span className={`video-check-status ${
                hasVideo === null ? 'pending' : 
                hasVideo ? 'available' : 'unavailable'
              }`}>
                {hasVideo === null ? '○' : hasVideo ? '✓' : '✗'}
              </span>
              <span>Видео доступно</span>
            </div>
            
            <div className="video-check-item">
              <span className={`video-check-status ${
                hasAudio === null ? 'pending' : 
                hasAudio ? 'available' : 'unavailable'
              }`}>
                {hasAudio === null ? '○' : hasAudio ? '✓' : '✗'}
              </span>
              <span>Аудио доступно</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTest;
